import PreviewFileModal from '@components/PreviewFileModal/PreviewFileModal';
import { SignedTemplate, Template } from '@features/digitalOnboarding';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Close from '@mui/icons-material/Close';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack, Link, Typography } from '@mui/material';
import { borderGrey, lightGrey, lightGrey2 } from '@styles/colors';
import { medium } from '@styles/fontWeights';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type TemplateListItemProps = {
  template: SignedTemplate;
  handleResend?: () => void;
  filePath: string | null;
  isApplicantView?: boolean;
  mainTemplateId?: number | null;
};

const TemplateListItem: FC<TemplateListItemProps> = ({
  template,
  handleResend = () => {},
  filePath,
  isApplicantView = false,
  mainTemplateId,
}) => {
  const navigate = useNavigate();
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFile = async (
    fileUrl: string | URL | Request,
    fileName: string | undefined,
  ) => {
    setIsDownloading(true);
    try {
      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: {
          // Optionally add headers if neede
        },
      });
      const blob = await response.blob();
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error downloading the file:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Stack
      spacing={2}
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems="center"
      sx={{
        p: 3,
        borderRadius: 5,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: borderGrey,
      }}
    >
      <Typography fontWeight={medium} display="flex" alignItems="center">
        {!isApplicantView ? (
          <>
            <CheckCircleIcon
              color={filePath ? 'success' : 'disabled'}
              sx={{ mr: 1 }}
            />
            <Stack>
              {template?.template?.name}
              <Typography fontSize="12px" color={lightGrey2}>
                Expires {dayjs(template?.date_expires).format('MM/DD')}
              </Typography>
              {template?.supplemental_file_path && (
                <a
                  href={template.supplemental_file_path}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button sx={{ fontSize: '12px', textTransform: 'none' }}>
                    Supplemental File(s)
                  </Button>
                </a>
              )}
            </Stack>
          </>
        ) : (
          isApplicantView && (
            <>
              {filePath ? (
                <CheckCircleIcon
                  color={filePath ? 'success' : 'disabled'}
                  sx={{ mr: 1 }}
                />
              ) : (
                <Close
                  color={filePath ? 'success' : 'disabled'}
                  sx={{ mr: 1 }}
                />
              )}
              <Box
                component="span"
                sx={{
                  cursor: !filePath ? 'pointer' : '',
                  textDecoration: !filePath ? 'underline' : '',
                }}
                // onClick={() => {
                //   !filePath &&
                //     window.open(
                //       `${window.location.origin}/applicants/documents/${mainTemplateId}/sign/`,
                //       '_blank',
                //     );
                // }}
              >
                {!filePath ? (
                  <Link
                    href={`${window.location.origin}/applicants/documents/${mainTemplateId}/sign/`}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      textDecoration: !filePath ? 'underline' : '',
                      color: '#2B2D40',
                      '&:hover': {
                        color: '#000000',
                      },
                    }}
                  >
                    {template?.name}
                  </Link>
                ) : (
                  <span>{template?.name}</span>
                )}
                {/* {template?.name} */}
              </Box>
            </>
          )
        )}
      </Typography>
      <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          startIcon={<RemoveRedEyeOutlinedIcon />}
          disabled={!filePath}
          onClick={() => setSelectedFilePath(filePath)}
        >
          Preview
        </Button>
        {!isApplicantView && (
          <>
            <LoadingButton
              loading={isDownloading}
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<DownloadForOfflineOutlinedIcon />}
              disabled={!filePath || isDownloading}
              onClick={() =>
                downloadFile(
                  filePath as string,
                  `${template?.template.name}.pdf`,
                )
              }
              // onClick={() => saveAs(filePath as string, `${template?.template.name}.pdf`)}
            >
              Download
            </LoadingButton>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<EmailOutlinedIcon />}
              onClick={handleResend}
            >
              Resend
            </Button>
          </>
        )}
      </Stack>

      <PreviewFileModal
        filePath={selectedFilePath}
        handleClose={() => setSelectedFilePath(null)}
        addS3Path
      />
    </Stack>
  );
};

export default TemplateListItem;
